import React, { useCallback, useEffect, useState, useRef } from 'react';
const CARD_WIDTH = 320;
const CARD_GAP = 32;
const CARD_STEP = CARD_WIDTH + CARD_GAP;
const TARGET_POSITION = 2; // Target card should land as 3rd visible card (index 2)
export function DifferenceSection() {
  const [highlightedTitle, setHighlightedTitle] = useState<string | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const isAnimatingRef = useRef(false);
  const differences = [{
    image: 'https://cdn.magicpatterns.com/uploads/sDy4AhW5tz7Genfn19qVTs/Gemini_Generated_Image_g61i82g61i82g61i.png',
    title: "My favorite café isn't on the list",
    fullImage: true,
    description: "We believe in carefully curating our café partners — selecting each one thoughtfully. So, during our launch, we've limited our spots to a select few. Don't see your favorite? Tell us. We'll work on bringing them onto Kraya so you can enjoy their perks too. And if they pass our vibe check — you'll get rewarded for the recommendation. 😉"
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/v2wHrfoiGqfnnXsavrafi6/83e258b62bd6cbfdabb61e477474a652.jpg',
    title: "What's the catch?",
    description: 'Every café already spends independently on marketing to attract and retain customers. Kraya brings them onto one platform — helping them reach more people and reduce those costs. Instead of scattered efforts, everything works in one place — so businesses grow, and you get rewarded. Everyone wins!',
    fullImage: true
  }, {
    video: 'https://cdn.magicpatterns.com/uploads/oyNcofj46inJiEGsmvRpbu/From KlickPin CF Wallet _ Motion graphics inspiration Motion design animation Motion design video.mp4',
    title: 'Truly Automatic',
    fullImage: true,
    description: 'When you visit our café partners, Kraya recognizes your visit and applies rewards automatically. No coupon codes. No extra steps. Our technology handles everything in the background. Just walk in, pay, and enjoy!'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/rLVxZGkthwicVffavsFGoN/5c50129ef275e615db2025c666b6c1ef.jpg',
    title: 'Gift an Experience',
    description: "See a friend having a rough day? Send them a coffee treat at their favorite café — right from the app. No silly gift cards. Just pick a spot, choose a treat, and they get a real surprise waiting for them. Got a crush on someone from college? Send them a surprise treat anonymously. Gifting on Kraya is personal, instant, and IRL. Make someone's day!!",
    fullImage: true
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/bEgQR3Z79AD254QQvJos9K/Gemini_Generated_Image_saha71saha71saha.png',
    title: 'Invite-only club',
    description: "Right now, Kraya is invite-only. How do you get in? We drop exclusive invites across colleges, social media, and IRL promotions — so there's a good chance someone you know already has one. Each user can share up to 3 invites with close friends. Ask around. One of your friends might already have one.",
    fullImage: true
  }, {
    video: 'https://cdn.magicpatterns.com/uploads/7deWnbEHKBB3MQGyKgnxrh/From KlickPin CF Smart curly hair care ideas for practical inspiration you can actually use today with simple details that elevate the final look - Pin-1152288254665025356.mp4',
    title: 'Surprise Perks',
    fullImage: true,
    description: 'Unlock exclusive menu items and secret discounts just by showing up. Sometimes you just want a little "me-time" at your favorite café — we get that. So every now and then, we drop surprise offers when you least expect it. Whether it\'s a random Tuesday at home or right when you walk into one of our café partners — check your phone. You might have something waiting. Keep checking, keep winning.'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/c6bjCdMp4z2ZfSq7wLTe5F/b3bb99cbc111abc275b068b65f9aa607.jpg',
    title: 'Chill with Friends',
    description: "Kraya is designed to be even more fun with friends. We've crafted group experiences like planning outings together, shopping together, unlocking group discounts, sharing your favorites, sending treats, and even swapping perks. Download now and have fun!",
    fullImage: true
  }];
  const privacyFirstIndex = differences.findIndex(d => d.title === "What's the catch?");
  const clearHighlight = useCallback(() => {
    setHighlightedTitle(null);
    // Resume CSS animation
    const el = carouselRef.current;
    if (el) {
      el.style.transform = '';
      el.style.animation = '';
      el.classList.add('animate-scroll-left');
    }
    // Reset card styles
    document.querySelectorAll('[data-faq-card]').forEach(card => {
      const htmlCard = card as HTMLElement;
      htmlCard.classList.remove('card-highlighted');
      htmlCard.style.opacity = '';
    });
  }, []);
  const triggerHighlight = useCallback((targetTitle: string) => {
    if (isAnimatingRef.current) return;
    isAnimatingRef.current = true;
    const el = carouselRef.current;
    if (!el) return;
    // Step 1: Pause and capture current position
    const computed = window.getComputedStyle(el);
    const matrix = new DOMMatrixReadOnly(computed.transform);
    const currentX = matrix.m41;
    // Remove CSS animation and set current position as inline style
    el.classList.remove('animate-scroll-left');
    el.style.animation = 'none';
    el.style.transform = `translateX(${currentX}px)`;
    // Step 2: Calculate target position (target card as 3rd visible card)
    const targetIndex = differences.findIndex(d => d.title === targetTitle);
    const targetX = -(targetIndex - TARGET_POSITION) * CARD_STEP;
    // Step 3: Animate card-by-card to target
    const startX = currentX;
    const distance = targetX - startX;
    const duration = Math.min(Math.abs(distance) * 2, 2000);
    const startTime = performance.now();
    function easeInOutCubic(t: number): number {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }
    function animate(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeInOutCubic(progress);
      const x = startX + distance * eased;
      el.style.transform = `translateX(${x}px)`;
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Step 4: Apply highlight
        isAnimatingRef.current = false;
        setHighlightedTitle(targetTitle);
        document.querySelectorAll('[data-faq-card]').forEach(card => {
          const htmlCard = card as HTMLElement;
          if (htmlCard.dataset.faqCard === targetTitle) {
            htmlCard.classList.add('card-highlighted');
          } else {
            htmlCard.style.opacity = '0.4';
          }
        });
      }
    }
    requestAnimationFrame(animate);
  }, []);
  // Listen for trigger from StepsSection
  useEffect(() => {
    const handler = (e: Event) => {
      const title = (e as CustomEvent).detail || "What's the catch?";
      setTimeout(() => triggerHighlight(title), 800);
    };
    window.addEventListener('trigger-faq-highlight', handler);
    return () => window.removeEventListener('trigger-faq-highlight', handler);
  }, [triggerHighlight]);
  // Dismiss highlight when user scrolls away
  useEffect(() => {
    if (!highlightedTitle || !sectionRef.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        clearHighlight();
      }
    }, {
      threshold: 0.2
    });
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [highlightedTitle, clearHighlight]);
  return <section ref={sectionRef} id="faq" className="py-24 bg-[#FFFC00] border-y-4 border-[#1A1A1A] overflow-hidden" data-id="element-111">
      <style data-id="element-112">{`
        @keyframes scroll-left {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll-left {
          animation: scroll-left 40s linear infinite;
        }
        @keyframes card-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,252,0,0.6), 8px 8px 0px 0px rgba(26,26,26,1); }
          50% { box-shadow: 0 0 24px 10px rgba(255,252,0,0.8), 8px 8px 0px 0px rgba(26,26,26,1); }
        }
        .card-highlighted {
          transform: scale(1.15) !important;
          z-index: 10;
          box-shadow: 0 0 24px 10px rgba(255,252,0,0.8), 8px 8px 0px 0px rgba(26,26,26,1);
          transition: transform 0.5s ease, box-shadow 0.5s ease;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16" data-id="element-113">
        <div className="text-center" data-id="element-114">
          <h2 className="text-4xl sm:text-6xl font-black uppercase tracking-tight text-[#1A1A1A]" data-id="element-115">
            What Makes Us Different
          </h2>
        </div>
      </div>

      <div className="relative w-full flex overflow-x-hidden overflow-y-visible py-16" data-id="element-116">
        <div ref={carouselRef} className="flex items-start w-max animate-scroll-left gap-8 pr-8" data-id="element-117">
          {[...differences, ...differences].map((item, index) => <div key={index} data-faq-card={item.title} className="w-[320px] shrink-0 bg-white rounded-[2rem] border-4 border-[#1A1A1A] shadow-[8px_8px_0px_0px_rgba(26,26,26,1)] flex flex-col transition-opacity duration-500 relative" data-id="element-118">
              <div className={`-mt-12 mx-3 rounded-2xl overflow-hidden border-4 border-[#1A1A1A] shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] ${(item as any).fullImage ? '' : ''}`} data-id="element-119">
                {(item as any).video ? <video src={(item as any).video} className={`w-full ${(item as any).fullImage ? 'object-contain' : 'h-[280px] object-cover'}`} autoPlay loop muted playsInline data-id="element-120" /> : <img src={(item as any).image} alt={item.title} className={`w-full ${(item as any).fullImage ? 'object-contain' : 'h-[280px] object-cover'}`} draggable={false} data-id="element-121" />}
              </div>
              <div className="p-6 flex-1 flex flex-col bg-white rounded-b-[calc(2rem-4px)]" data-id="element-122">
                <h3 className="text-2xl font-black mb-3 text-[#1A1A1A] uppercase leading-tight" data-id="element-123">
                  {item.title}
                </h3>
                <p className="text-gray-700 text-base font-medium leading-relaxed" data-id="element-124">
                  {item.description}
                </p>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}